/**
 * Lossless synchronous wire encoding. Every key, value, photo, price, offer and
 * ordering is retained. Ordinary historic JSON seeds remain readable.
 * Tagged arrays distinguish actual arrays from objects and string references.
 */
export function encodeLandingSeed(value) {
  const keys = [], strings = [], keyIds = new Map(), stringIds = new Map();
  const intern = (text, values, ids) => {
    if (!ids.has(text)) { ids.set(text, values.length); values.push(text); }
    return ids.get(text);
  };
  const visit = (entry) => {
    if (typeof entry === "string" && entry.length > 24) return [2, intern(entry, strings, stringIds)];
    if (Array.isArray(entry)) return [1, entry.map(visit)];
    if (entry && typeof entry === "object") return [0, Object.entries(entry).map(([key, child]) =>
      [intern(key, keys, keyIds), visit(child)])];
    return entry;
  };
  // Match JSON's treatment of undefined, prototypes and toJSON once up front.
  const normalized = JSON.parse(JSON.stringify(value));
  const wire = { landingSeedWire: 1, keys, strings, data: visit(normalized) };
  return JSON.stringify(wire).length < JSON.stringify(normalized).length ? wire : normalized;
}

export function decodeLandingSeed(value) {
  if (!value || value.landingSeedWire === undefined) return value;
  if (value.landingSeedWire !== 1 || !Array.isArray(value.keys) || !Array.isArray(value.strings) ||
      value.keys.some((key) => typeof key !== "string") || value.strings.some((text) => typeof text !== "string")) {
    throw new Error("Invalid compact landing seed");
  }
  let nodes = 0;
  const visit = (entry, depth = 0) => {
    if (++nodes > 200000 || depth > 100) throw new Error("Compact landing seed exceeds bounds");
    if (!Array.isArray(entry)) {
      if (entry && typeof entry === "object") throw new Error("Invalid compact landing seed node");
      return entry;
    }
    if (entry.length !== 2) throw new Error("Invalid compact landing seed tag");
    if (entry[0] === 2 && Number.isInteger(entry[1]) && typeof value.strings[entry[1]] === "string") return value.strings[entry[1]];
    if (entry[0] === 1 && Array.isArray(entry[1])) return entry[1].map((child) => visit(child, depth + 1));
    if (entry[0] === 0 && Array.isArray(entry[1])) {
      const seen = new Set();
      return Object.fromEntries(entry[1].map((pair) => {
        if (!Array.isArray(pair) || pair.length !== 2 || !Number.isInteger(pair[0]) ||
            typeof value.keys[pair[0]] !== "string" || seen.has(pair[0])) throw new Error("Invalid compact landing seed key");
        seen.add(pair[0]);
        return [value.keys[pair[0]], visit(pair[1], depth + 1)];
      }));
    }
    throw new Error("Invalid compact landing seed reference");
  };
  return visit(value.data);
}
